import { RequestDocumentsForm } from "@components/RequestDocumentsForm";
import { Skeleton } from "@components/ui/skeleton";

import { useCaseByCaseNumber } from "@hooks/queries/use-cases";
import { useCaseDocumentRequests } from "@hooks/queries/use-provider-requests";

import { RequestStatusCard } from "./RequestStatusCard";

/**
 * The "Request documents from a provider" content — the request form until a
 * request exists (only-once), then the records-request status card with the
 * check-for-updates poll. Shared by the doctor's Request Documents tab
 * (`RequestDocumentsPage`) and the referrer's case-detail section (rendered
 * inline under Case Details), so both stay in sync.
 */
export const RequestDocumentsSection = ({ caseNumber }: { caseNumber: string }) => {
  const { data: caseData } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });
  const caseId = caseData?.id;

  const { data: requests, isLoading } = useCaseDocumentRequests({ caseId });
  const existing = requests?.[0];

  return (
    <div className="rounded-xl border border-border bg-background-white">
      <div className="border-b border-border p-6">
        <h2 className="text-xl font-bold text-foreground">
          Request documents from a provider
        </h2>
        <p className="mt-1 text-sm text-foreground-muted">
          Request the full bundle, or specific documents by type, from a records
          provider for this case.
        </p>
      </div>

      <div className="p-6">
        {isLoading || !caseId ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : existing ? (
          <RequestStatusCard caseId={caseId} request={existing} />
        ) : (
          <RequestDocumentsForm caseId={caseId} caseNumber={caseNumber} />
        )}
      </div>
    </div>
  );
};
